import { Component, Inject } from '@angular/core';
import { FormGroup, Validators } from '@angular/forms';
import { MatDialogRef, MAT_DIALOG_DATA } from '@angular/material/dialog';
import { TemplateService } from 'src/app/core/services/template.service';
import { UtilService } from 'src/app/core/services/util.service';
import { ApproachProfileService } from 'src/app/core/services/approach-profile.service';
import { UploadSummary } from '../../partials/file-upload/file-upload.component';

@Component({
  selector: 'app-template',
  templateUrl: './template.component.html',
  styleUrl: './template.component.scss'
})
export class TemplateComponent {

  action: 'add' | 'edit' = 'add';
  rowData: any = null;

  userForm: FormGroup = new FormGroup({});
  formFields = [
    { name: 'displayName', label: 'Display Name', type: 'text', space: 1, validators: [Validators.required] },
    { name: 'templateType', label: 'Template Type', type: 'select', space: 1, validators: [Validators.required], options: [
      { label: 'Summary', value: 'summary' },
      { label: 'Application', value: 'application' }
    ]},
    { name: 'approach', label: 'Approach', type: 'select', space: 1, options: [] as any[] },
  ];

  /** Shared templates (app.pdf, teams.pdf) legitimately have no approach. */
  private readonly noApproachOption = { label: '(None - shared template)', value: '' };
  uploadSummary: UploadSummary[] = [];
  submitting = false;

  /** (approach, templateType) pairs already taken, so they can be disabled per selected type. */
  private takenByType = new Map<string, Set<string>>();

  /**
   * A new template is meaningless without a file, and the row is created either way, so the file
   * is required on add. Matches getQueuedFileContents, which only reads 'queue' entries - gating
   * on anything else would let Submit disagree with what submit() actually accepts.
   * On edit the existing file is kept when no new one is chosen.
   */
  get hasQueuedFile(): boolean {
    return this.uploadSummary.some(f => f.status === 'queue');
  }

  get fileRequiredMissing(): boolean {
    return this.action === 'add' && !this.hasQueuedFile;
  }

  constructor(
    public dialogRef: MatDialogRef<TemplateComponent>,
    @Inject(MAT_DIALOG_DATA) public data: any,
    private templateService: TemplateService,
    private utilService: UtilService,
    private approachProfileService: ApproachProfileService
  ) {
    this.action = data?.action || 'add';
    this.rowData = data?.data || null;
  }

  ngOnInit() {
    this.loadExistingTemplates();
    this.loadApproachOptions();
  }

  /**
   * Only one template may exist per (approach, templateType): GetByApproachAndTypeAsync takes
   * FirstOrDefault over an unordered scan, so a duplicate makes the served document
   * nondeterministic. Taken approaches are disabled rather than rejected on submit.
   */
  private loadExistingTemplates() {
    this.templateService.getTemplates().subscribe({
      next: (res: any) => {
        (res || []).forEach((t: any) => {
          if (!t?.approach || !t?.templateType) return;
          if (t.templateFileId === this.rowData?.templateFileId) return;
          const type = t.templateType.toLowerCase();
          if (!this.takenByType.has(type)) this.takenByType.set(type, new Set());
          this.takenByType.get(type)!.add(this.normalizeApproach(t.approach));
        });
        this.applyTakenState();
      },
      error: () => { /* non-fatal: the handler rejects duplicates with a 409 regardless */ }
    });
  }

  /** Mirrors GetByApproachAndTypeAsync: underscores to spaces, trimmed, case-insensitive. */
  private normalizeApproach(approach: string) {
    return (approach || '').replace(/_/g, ' ').trim().toLowerCase();
  }

  /** Disables approaches already used for the currently selected template type. */
  private applyTakenState() {
    const type = (this.userForm.get('templateType')?.value || '').toLowerCase();
    const taken = this.takenByType.get(type) || new Set<string>();
    const field: any = this.formFields.find(f => f.name === 'approach');
    if (!field?.options) return;

    field.options = field.options.map((o: any) => ({
      ...o,
      disabled: !!o.value && taken.has(this.normalizeApproach(o.value)),
    }));
  }

  /**
   * Binds the approach field to real approach profiles. A free-text approach that does not match
   * a profile silently breaks template resolution - the download finds no template and the
   * summary sheet is never sent, with only a warning in the logs.
   *
   * Profiles are per renewal year but a template's approach is not, so the list is de-duplicated
   * by name. An existing value that matches no profile (older rows use LEA_RENEWAL where the
   * profile is "LEA Renewal") is kept as an option so editing another field cannot silently
   * change or clear it.
   */
  private loadApproachOptions() {
    this.approachProfileService.getProfiles({}).subscribe({
      next: (res: any) => {
        const byName = new Map<string, string>();
        (res || []).forEach((p: any) => {
          if (p?.ApproachName && !byName.has(p.ApproachName)) {
            byName.set(p.ApproachName, p.DisplayName || p.ApproachName);
          }
        });

        const options = [...byName.entries()]
          .map(([value, label]) => ({ label: label === value ? value : `${label} (${value})`, value }))
          .sort((a, b) => a.label.localeCompare(b.label));

        this.setApproachOptions([this.noApproachOption, ...options]);
      },
      error: () => {
        // Never trap the admin in a dialog with an empty dropdown - fall back to the current value.
        this.setApproachOptions([this.noApproachOption]);
        this.utilService.openToastr('error', 'Could not load approaches');
      }
    });
  }

  private setApproachOptions(options: any[]) {
    const current = this.rowData?.approach;
    if (current && !options.some(o => o.value === current)) {
      options = [...options, { label: `${current} (not a current approach)`, value: current }];
    }
    const field = this.formFields.find(f => f.name === 'approach');
    if (field) field.options = options;
    this.applyTakenState();
  }

  ngAfterViewInit() {
    if (this.action === 'edit' && this.rowData) {
      setTimeout(() => {
        this.userForm.patchValue({
          displayName: this.rowData.displayName || '',
          templateType: this.rowData.templateType || '',
          approach: this.rowData.approach || '',
        });
      });
    }

    // Which approaches are taken depends on the selected type, so re-evaluate when it changes.
    setTimeout(() => {
      this.userForm.get('templateType')?.valueChanges.subscribe(() => {
        this.applyTakenState();
        const approach: any = this.userForm.get('approach');
        const opt = (this.formFields.find(f => f.name === 'approach') as any)
          ?.options?.find((o: any) => o.value === approach?.value);
        if (opt?.disabled) approach.setValue('');
      });
    });
  }

  async submit() {
    if (!this.userForm.valid) return;
    const files = await Promise.all(this.utilService.getQueuedFileContents(this.uploadSummary));
    if (this.action === 'add' && !files.length) {
      this.utilService.openToastr('error', 'Please select a file to upload');
      return;
    }

    const payload:any = {
      displayName: this.userForm.value.displayName,
      templateType: this.userForm.value.templateType,
      approach: this.userForm.value.approach || null,
    };

    if(this.rowData?.templateFileId){
      payload.templateFileId = this.rowData.templateFileId;
    }

    if (files.length) {
      payload.fileName = files[0].name;
      payload.fileContent = files[0].fileContentBase64;
    }

    this.submitting = true;
    this.createTemplate(payload)
  }

  createTemplate(payload:any) {
    let request;
    if(this.action === 'add'){
      this.templateService.createTemplate(payload).subscribe({
        next: () => {
          this.utilService.openToastr('success', 'Template file created');
          this.dialogRef.close(true);
        },
        error: () => {
          this.utilService.openToastr('error', 'Failed to create template file');
          this.submitting = false;
        }
      });
    }else{
      this.templateService.updateTemplate(payload).subscribe({
        next: () => {
          this.utilService.openToastr('success', 'Template file updated');
          this.dialogRef.close(true);
        },
        error: () => {
          this.utilService.openToastr('error', 'Failed to update template file');
          this.submitting = false;
        }
      });
    }
  }

  
}
